import { BaseApiService } from './baseApiService';
import { storage } from '../../utils/storage';
import { environment } from '../../config/environment';

interface AuthResponse {
  status: string;
  message: string;
  data: {
    access_token: string;
    refresh_token: string;
    user: {
      id: number;
      phone_no: string;
      is_verified: boolean;
      is_patient: boolean;
    };
    session: {
      id: number;
      status: string;
      start_time: string;
    };
  };
}

interface LoginResponse {
  status: string;
  message: string;
}

interface OTPGenerateResponse {
  status: string;
  message: string;
}

interface OTPValidateResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    phone_no: string;
    is_verified: boolean;
    is_patient: boolean;
  };
}

// New interfaces for magic link flow
interface MagicLinkVerifyResponse {
  status: string;
  message: string;
  data: {
    session_id: number;
    patient_id: number;
    otp_sent: boolean;
    phone_no: string;
    is_external?: boolean;
    auth?: MagicLinkOTPResponse;
    specialty?: string;
    language?: string;
    multilingual_consult?: boolean;
  };
}

interface MagicLinkOTPResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  refresh_token: string;
  user_info: {
    name: string;
    email: string;
    phone_no: string;
    age: string;
    gender: string;
    country: string;
    clinic_id: number;
    is_admin: boolean;
    is_super_admin: boolean;
    is_patient: boolean;
    is_medical_officer: boolean;
  };
}

interface CreateSessionResponse {
  status: string;
  message?: string;
  data: {
    session_id: string;
    session?: {
      id: number;
      status: string;
    };
    user?: {
      id: number;
      phone_no: string;
      is_verified: boolean;
      is_patient: boolean;
      is_medical_officer: boolean;
    };
  };
}

class AuthService extends BaseApiService {
  private readonly CLIENT_ID = environment.CLIENT_ID;

  async generateOTP(phone_no: string): Promise<OTPGenerateResponse> {
    const response = await fetch(`${this.authUrl}/auth/otp/generate/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone_no }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async validateOTP(phone_no: string, otp: string): Promise<OTPValidateResponse> {
    const response = await fetch(`${environment.LOGIN_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone_no,
        otp,
        client_id: this.CLIENT_ID
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('OTP validation error:', errorData);
      throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('OTP validation response:', data);

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: {
        id: data.user_id || 0,
        phone_no: phone_no,
        is_verified: true,
        is_patient: true
      }
    };
  }

  // New magic link verification method
  async verifyMagicLink(magicLinkToken: string, sessionType?: string): Promise<MagicLinkVerifyResponse> {
    console.log('🚀 API: verifyMagicLink called');

    const requestBody: any = { magic_link_token: magicLinkToken };

    // If session_type is provided (for meeting links), include it
    if (sessionType) {
      requestBody.session_type = sessionType;
    }

    const response = await fetch(`${this.baseUrl}/verify-magic-link/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Magic link verification error:', errorData);
      throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Magic link verified successfully');

    if (data.data?.otp_sent) {
      console.log('📱 OTP sent to:', data.data.phone_no);
    }

    return data;
  }

  // New magic link OTP validation method
  async validateMagicLinkOTP(phone_no: string, otp: string, patientId?: number): Promise<MagicLinkOTPResponse> {
    const response = await fetch(`${environment.LOGIN_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone_no,
        otp,
        client_id: this.CLIENT_ID,
        ...(patientId && { user_id: patientId })
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Magic link OTP validation error:', errorData);
      throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Magic link OTP validation response:', data);
    return data;
  }

  async createSession(): Promise<CreateSessionResponse> {
    return this.fetchWithAuth('/create-session/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async login(
    phone_no: string,
    user_type: string = 'patient'
  ): Promise<LoginResponse> {
    const encryptedData = this.encrypt({ phone_no, user_type });

    const response = await fetch(`${this.baseUrl}/api/v1/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: encryptedData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async logout(): Promise<void> {
    // await this.fetchWithAuth('/api/v1/logout/', {
    //   method: 'POST',
    // });
    storage.clearAuthData();
  }
}

export const authService = new AuthService();
